import { X_MAX_TWEET_LENGTH } from '@postpilot/core/domain';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

/**
 * The circular character counter, as X itself does it.
 *
 * Why a ring and not a number: at 40/280 the exact count is irrelevant and a
 * number is noise. At 275/280 it is the only thing that matters. A ring conveys
 * "you have room" at a glance and escalates to a precise number exactly when
 * precision starts to matter — so the UI gets louder as the stakes rise.
 *
 * The count comes from `countTweetLength`, which weights URLs at 23 and emoji at
 * 2 the way X does. A naive `text.length` would show 100 for a link that X
 * counts as 23, and the user would rewrite a post that was never too long.
 */
export function CharacterRing({ count, className }: { count: number; className?: string }) {
  const remaining = X_MAX_TWEET_LENGTH - count;
  const progress = Math.min(count / X_MAX_TWEET_LENGTH, 1);

  const isOver = remaining < 0;
  const isClose = remaining <= 20 && !isOver;

  const RADIUS = 10;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

  const color = isOver
    ? 'stroke-status-failed'
    : isClose
      ? 'stroke-status-processing'
      : 'stroke-primary';

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* The exact number appears only when it starts to matter. */}
      {(isClose || isOver) && (
        <motion.span
          initial={{ opacity: 0, x: 4 }}
          animate={{ opacity: 1, x: 0 }}
          className={cn(
            'font-mono text-xs tabular-nums',
            isOver ? 'text-status-failed font-semibold' : 'text-muted-foreground',
          )}
        >
          {remaining}
        </motion.span>
      )}

      <svg width="24" height="24" viewBox="0 0 24 24" className="-rotate-90" aria-hidden>
        <circle cx="12" cy="12" r={RADIUS} fill="none" strokeWidth="2" className="stroke-border" />
        <circle
          cx="12"
          cy="12"
          r={RADIUS}
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={`${progress * CIRCUMFERENCE} ${CIRCUMFERENCE}`}
          className={cn(color, 'transition-dasharray')}
        />
      </svg>

      {/* The visual ring is aria-hidden, so the count has to reach a screen
          reader some other way. `polite` — announcing every keystroke would be
          unusable, so the reader batches it. */}
      <span className="sr-only" role="status" aria-live="polite">
        {isOver
          ? `${Math.abs(remaining)} characters over the limit`
          : `${remaining} characters remaining`}
      </span>
    </div>
  );
}
